import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router';

import { Currency } from 'views/bluviu-nutrition/features/scenarios/types/scenario.type';

import { useAccountState } from 'contexts/account/account-context';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import isScenarioDisabled from 'views/bluviu-nutrition/features/scenarios/utils/validate-scenario';

import { usePost } from 'hooks/use-post';
import { putScenarioFeedProgram } from 'views/bluviu-nutrition/features/scenarios/api/bluviu-nutrition';

import Collapsible from 'components/collapsible';
import FilterSidebar from 'components/filter-sidebar';
import LoadingSpinner from 'components/loading-spinner';
import RadioSelectItem from 'components/radio-select-item';
import Select from 'components/select';
import ScenarioFeedProgram from 'views/bluviu-nutrition/features/scenarios/components/edit-scenario/scenario-feed-program';
import { useScenarioState } from 'views/bluviu-nutrition/features/scenarios/context/scenario-context';
import { Scenario } from 'views/bluviu-nutrition/features/scenarios/types/scenario.type';
import { UpdateScenarioFeedProgramRequest } from 'views/bluviu-nutrition/features/scenarios/types/update-scenario-feed-program-request.type';
import { ScenariosParams } from '../../types/scenarios-params.type';
import { Box } from '@mui/material';

type FeedProgramsProps = {
  isLoading: boolean;
};

const PAGE_NAME_ID = 'bluViuNutrition.availableFeedsEEGt2P';

const FeedPrograms: React.FC<FeedProgramsProps> = ({ isLoading }) => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const params = useParams<ScenariosParams>();
  const { account } = useAccountState();
  const snackbarDispatch = useSnackbarDispatch();
  const {
    scenarioQuery: { data: scenario, isLoading: isScenarioLoading },
  } = useScenarioState();

  const [currency, setCurrency] = useState<Currency>(Currency.NOK);

  if (isScenarioLoading) return <LoadingSpinner />;
  if (!scenario) return;

  // #region POST DATA
  const updateFeedProgram = usePost<
    Scenario,
    { globalCompanyId: string; scenarioId: string; request: UpdateScenarioFeedProgramRequest }
  >(
    (request) => putScenarioFeedProgram(request.globalCompanyId, request.scenarioId, request.request),
    (response) => {
      queryClient.setQueryData(['getScenario', account.company?.companyId, params.scenarioId], response);
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );
  // #endregion POST DATA

  // #region HANDLERS
  const handleUpdateAvailableFeedProgram = (request: UpdateScenarioFeedProgramRequest) => {
    updateFeedProgram.mutate({
      globalCompanyId: scenario.globalCompanyId,
      scenarioId: scenario.id,
      request: {
        ...request,
        currency: currency,
      },
    });
  };
  // #endregion HANDLERS

  const renderCurrency = () => {
    return Object.entries(Currency).map((type) => (
      <RadioSelectItem key={type[0]} value={type[1]} onClick={() => setCurrency(type[1])}>
        <FormattedMessage id={`${PAGE_NAME_ID}.select.${type[0]}`} defaultMessage={type[1]} />
      </RadioSelectItem>
    ));
  };

  return (
    <Collapsible label={intl.formatMessage({ id: `${PAGE_NAME_ID}.title` })} hideBorder open={true}>
      <Box display={'flex'} flexDirection={'column'} gap={4}>
        <FilterSidebar
          sx={{
            width: '50%',
          }}>
          <Select
            dark
            fullWidth
            value={currency}
            sx={{ m: { xs: 2, md: 0 } }}
            disabled={isScenarioDisabled(scenario, isLoading)}
            label={intl.formatMessage({ id: `${PAGE_NAME_ID}.select.currency` })}>
            {renderCurrency()}
          </Select>
        </FilterSidebar>
        <ScenarioFeedProgram isUpdating={updateFeedProgram.isPending} submit={handleUpdateAvailableFeedProgram} />
      </Box>
    </Collapsible>
  );
};
export default React.memo(FeedPrograms);
